import React, { useState } from 'react';

import Display from '@dashboard/components/Display';
import KeyChip from '@dashboard/components/KeyChip';
import RecipientsChipsList from '@dashboard/components/RecipientChipsList';
import { getEmailMessageSourceLabel } from '@dashboard/models/EmailMessage';
import { ExpandLessOutlined, ExpandMoreOutlined } from '@mui/icons-material';
import { Box, Button, List, Typography } from '@mui/material';

import ContactsGroupLink from './ContactsGroupLink';
import EmailMessagesBatchLink from './EmailMessagesBatchLink';
import PreambleField from './PreambleField';
import TemplateLink from './TemplateLink';
import WorkflowLink from './WorkflowLink';

type Props = {
  waypointEventId: string;
};
export default function MessagePreamble({ waypointEventId }: Props) {
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <Box sx={{ borderBottom: '1px solid', borderBottomColor: 'divider', p: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Message
      </Typography>
      <List
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          '& .MuiListItem-root': {
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 0, sm: 8 },
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            p: 0,
          },
        }}
      >
        <PreambleField waypointEventId={waypointEventId} label="Message ID">
          {(event) => <KeyChip label={event.id} tooltipTitle="Copy ID to clipboard" />}
        </PreambleField>
        <PreambleField waypointEventId={waypointEventId} label="Meta ID">
          {(event) => event.metaId}
        </PreambleField>
        <PreambleField waypointEventId={waypointEventId} label="Source">
          {(event) => getEmailMessageSourceLabel(event.source)}
        </PreambleField>
        <PreambleField waypointEventId={waypointEventId} label="Template">
          {(event) => <TemplateLink waypointTemplateId={event.waypointTemplateId} />}
        </PreambleField>
        <PreambleField waypointEventId={waypointEventId} label="Workflow">
          {(event) => <WorkflowLink workflowRunId={event.sourceWorkflowRunId} />}
        </PreambleField>
        <PreambleField waypointEventId={waypointEventId} label="Group">
          {(event) => <ContactsGroupLink contactsGroupId={event.contactsGroupId} />}
        </PreambleField>
        <PreambleField waypointEventId={waypointEventId} label="Batch">
          {(event) => <EmailMessagesBatchLink emailMessagesBatchId={event.sourceEmailMessagesBatchId} />}
        </PreambleField>
        <PreambleField waypointEventId={waypointEventId} label="Subject">
          {(event) => event.message.subject}
        </PreambleField>
        <PreambleField waypointEventId={waypointEventId} label="Preheader">
          {(event) => event.message.preheader}
        </PreambleField>
        <PreambleField waypointEventId={waypointEventId} label="To">
          {(event) => <RecipientsChipsList text={event.message.to} />}
        </PreambleField>
        <PreambleField waypointEventId={waypointEventId} label="From">
          {(event) => event.message.from}
        </PreambleField>

        <Display display={!isExpanded}>
          <Box>
            <Button onClick={() => setIsExpanded(true)} startIcon={<ExpandMoreOutlined />} sx={{ mt: 1, ml: -1 }}>
              More
            </Button>
          </Box>
        </Display>

        <Display display={isExpanded}>
          <PreambleField waypointEventId={waypointEventId} label="CC">
            {(event) => <RecipientsChipsList text={event.message.cc} />}
          </PreambleField>
          <PreambleField waypointEventId={waypointEventId} label="BCC">
            {(event) => <RecipientsChipsList text={event.message.bcc} />}
          </PreambleField>
          <PreambleField waypointEventId={waypointEventId} label="Reply to">
            {(event) => event.message.replyTo}
          </PreambleField>
        </Display>
        <Display display={isExpanded}>
          <Box>
            <Button onClick={() => setIsExpanded(false)} startIcon={<ExpandLessOutlined />} sx={{ mt: 1, ml: -1 }}>
              Less
            </Button>
          </Box>
        </Display>
      </List>
    </Box>
  );
}
